import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import api from '../../services/api'
import { translateApiError } from '../../utils/errorMessages'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import { Button, FormGroup, Input, Select } from '../../components/ui'
import ConfirmModal from '../../components/ConfirmModal'
import { getDepartmentTone } from '../company/stage-budget/stageBudgetUtils'
import { getBudgetExpenseKind, getBudgetExpenseMarker, getBudgetExpenseOrder } from '../company/budgetExpenseVisuals'
import './Admin.css'

const CATEGORY_LABELS = {
  team_income: 'Доход команды',
  tax: 'Налог',
  non_production: 'Непроизводственные расходы',
  production: 'Производственные расходы'
}

const DEFAULT_EXPENSE_ROWS = [
  { team: 'Налоги (+5%)', distribution_category: 'tax', percentage: '5' },
  { team: 'Произв-ые расходы', distribution_category: 'production', percentage: '0' },
  { team: 'Непроиз-ые расходы', distribution_category: 'non_production', percentage: '0' }
]

const PRODUCTION_TYPE = 'Производственное'
const PRODUCTION_SECTION = 'Производство'
const SERVICE_SECTION = 'Обслуживание'

const buildDefaultCreateRows = (teamOptions) => {
  const rows = []
  const seen = new Set()

  for (const option of teamOptions || []) {
    const team = (option?.value || '').trim()
    if (!team) continue
    const key = team.toLowerCase()
    if (DEFAULT_EXPENSE_ROWS.some((expense) => normalizeKey(expense.team) === key)) continue
    if (seen.has(key)) continue
    seen.add(key)
    rows.push({ id: null, team, distribution_category: 'team_income', percentage: '0' })
  }

  for (const expenseRow of DEFAULT_EXPENSE_ROWS) {
    const team = expenseRow.team.trim()
    const key = team.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    rows.push({ id: null, ...expenseRow })
  }

  return rows.length > 0 ? rows : [{ id: null, team: '', distribution_category: 'team_income', percentage: '' }]
}

function normalizeKey(value) {
  return String(value || '').trim().toLowerCase()
}

function formatPercentTotal(value) {
  const number = Number(value) || 0
  return `${new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(number).replace(/\u00a0/g, ' ')}%`
}

function getCategoryLabel(categoryOptions, category) {
  return categoryOptions.find((option) => option.value === category)?.label || CATEGORY_LABELS[category] || category || '—'
}

function getTeamOptionMeta(teamOptions, team) {
  const key = normalizeKey(team)
  return (teamOptions || []).find((option) => normalizeKey(option.value || option.team_name) === key) || null
}

function hasSnapshotTeamMeta(row) {
  return Boolean(row?.department_name || row?.department_type || row?.department_id || row?.team_id)
}

function getBudgetRuleTeamMeta(row, teamOptions) {
  const option = getTeamOptionMeta(teamOptions, row?.team)
  if (hasSnapshotTeamMeta(row)) {
    return {
      departmentType: row?.department_type || option?.department_type || '',
      departmentName: row?.department_name || option?.department_name || '',
      departmentSortOrder: Number.isFinite(Number(row?.department_sort_order))
        ? Number(row.department_sort_order)
        : (Number.isFinite(Number(option?.department_sort_order)) ? Number(option.department_sort_order) : 999999),
      label: row?.team || option?.team_name || option?.value || '',
      isTeam: true,
    }
  }
  return {
    departmentType: option?.department_type || '',
    departmentName: option?.department_name || '',
    departmentSortOrder: Number.isFinite(Number(option?.department_sort_order)) ? Number(option.department_sort_order) : 999999,
    label: option?.team_name || option?.value || row?.team || '',
    isTeam: Boolean(option),
  }
}

function getSectionForTeamMeta(meta) {
  return meta.departmentType === PRODUCTION_TYPE ? PRODUCTION_SECTION : SERVICE_SECTION
}

function isDefaultExpenseRow(row) {
  const key = normalizeKey(row?.team)
  return DEFAULT_EXPENSE_ROWS.some((expense) => normalizeKey(expense.team) === key)
}

function getDefaultExpenseCategory(team) {
  return DEFAULT_EXPENSE_ROWS.find((expense) => normalizeKey(expense.team) === normalizeKey(team))?.distribution_category || ''
}

function isKnownTeamRow(row, teamOptions) {
  if (isDefaultExpenseRow(row)) return false
  return (row?.distribution_category || 'team_income') === 'team_income' && getBudgetRuleTeamMeta(row, teamOptions).isTeam
}

function isManualExpenseRow(row, teamOptions) {
  if ((row?.distribution_category || 'team_income') !== 'team_income') {
    return !isDefaultExpenseRow(row)
  }
  return !isKnownTeamRow(row, teamOptions)
}

function compareTeamRows(a, b, teamOptions) {
  const metaA = getBudgetRuleTeamMeta(a, teamOptions)
  const metaB = getBudgetRuleTeamMeta(b, teamOptions)
  const sectionRankA = metaA.departmentType === PRODUCTION_TYPE ? 0 : 1
  const sectionRankB = metaB.departmentType === PRODUCTION_TYPE ? 0 : 1
  return sectionRankA - sectionRankB
    || metaA.departmentSortOrder - metaB.departmentSortOrder
    || metaA.departmentName.localeCompare(metaB.departmentName, 'ru')
    || metaA.label.localeCompare(metaB.label, 'ru')
}

function compareExpenseRows(a, b) {
  return getBudgetExpenseOrder(getBudgetExpenseKind(a)) - getBudgetExpenseOrder(getBudgetExpenseKind(b))
    || String(a.team || '').localeCompare(String(b.team || ''), 'ru')
}

function buildBudgetRuleViewRows(rows, teamOptions) {
  const viewRows = []
  const teamRows = (rows || [])
    .filter((row) => (row.distribution_category || 'team_income') === 'team_income' && !isManualExpenseRow(row, teamOptions))
    .sort((a, b) => compareTeamRows(a, b, teamOptions))
  const expenseRows = (rows || [])
    .filter((row) => (row.distribution_category || 'team_income') !== 'team_income' || isManualExpenseRow(row, teamOptions))
    .sort(compareExpenseRows)

  let currentSection = null
  for (const row of teamRows) {
    const section = getSectionForTeamMeta(getBudgetRuleTeamMeta(row, teamOptions))
    if (currentSection !== section) {
      currentSection = section
      viewRows.push({ kind: 'section', key: `section-${section}`, label: section })
    }
    viewRows.push({ kind: 'row', row })
  }

  if (expenseRows.length) {
    if (currentSection !== SERVICE_SECTION) {
      viewRows.push({ kind: 'section', key: `section-${SERVICE_SECTION}`, label: SERVICE_SECTION })
    }
    expenseRows.forEach((row) => viewRows.push({ kind: 'row', row }))
  }

  return viewRows
}

function mapBudgetRuleRows(ruleRows, keepIds = true) {
  return (ruleRows || []).map((r) => ({
    id: keepIds ? r.id : null,
    team: r.team || '',
    distribution_category: getDefaultExpenseCategory(r.team) || r.distribution_category || 'team_income',
    percentage: r.percentage != null ? String(r.percentage) : '',
    team_id: r.team_id || null,
    department_id: r.department_id || null,
    department_name: r.department_name || '',
    department_type: r.department_type || '',
    department_sort_order: r.department_sort_order ?? null,
  }))
}

function BudgetRuleTypeForm() {
  const { distributionType: distributionTypeParam } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isCreateMode = !distributionTypeParam
  const decodedParamType = distributionTypeParam ? decodeURIComponent(distributionTypeParam) : ''
  const copySourceType = useMemo(() => {
    if (!isCreateMode) return ''
    return new URLSearchParams(location.search).get('copy_from') || ''
  }, [isCreateMode, location.search])

  const [loading, setLoading] = useState(true)
  const [copySourceLoaded, setCopySourceLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [typeName, setTypeName] = useState(decodedParamType || '')
  const [teamOptions, setTeamOptions] = useState([])
  const [categories, setCategories] = useState([])
  const [rows, setRows] = useState([])
  const [initialRows, setInitialRows] = useState([])
  const [deleteTargetId, setDeleteTargetId] = useState(null)

  usePageHeader({
    backLink: '/admin/budget/budget-rules',
    backLabel: '← К списку типов',
    title: isCreateMode ? 'Новый тип распределения' : `Тип распределения: ${typeName}`,
  }, [isCreateMode, typeName])

  const categoryOptions = useMemo(() => {
    if (categories.length > 0) {
      return categories.map((c) => ({ value: c.code, label: c.name }))
    }
    return Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }))
  }, [categories])
  const expenseCategoryOptions = useMemo(
    () => categoryOptions
      .filter((option) => option.value !== 'team_income')
      .sort((a, b) => getBudgetExpenseOrder(getBudgetExpenseKind({ distribution_category: a.value })) - getBudgetExpenseOrder(getBudgetExpenseKind({ distribution_category: b.value }))),
    [categoryOptions]
  )
  const viewRows = useMemo(() => buildBudgetRuleViewRows(rows, teamOptions), [rows, teamOptions])
  const percentageTotal = useMemo(
    () => rows.reduce((sum, row) => {
      const percentage = row.percentage === '' ? 0 : Number(row.percentage)
      return sum + (Number.isFinite(percentage) ? percentage : 0)
    }, 0),
    [rows]
  )

  useEffect(() => {
    let mounted = true
    async function load() {
      try {
        setLoading(true)
        setError('')
        setCopySourceLoaded(false)
        const [teamRes, categoryRes] = await Promise.all([
          api.get('/admin/budget/team-options'),
          api.get('/admin/budget/distribution-categories')
        ])
        if (!mounted) return
        const loadedTeamOptions = teamRes.data?.team_options || []
        setTeamOptions(loadedTeamOptions)
        setCategories(categoryRes.data?.distribution_categories || [])

        if (isCreateMode) {
          if (copySourceType) {
            const rulesRes = await api.get('/admin/budget/budget-rules')
            if (!mounted) return
            const groups = rulesRes.data?.grouped || []
            const source = groups.find((g) => g.distribution_type_id === copySourceType)
              || groups.find((g) => g.distribution_type === copySourceType)
            if (!source) {
              setError('Тип распределения для копирования не найден')
              setTypeName('')
              setRows([])
              setInitialRows([])
              return
            }
            const copiedRows = mapBudgetRuleRows(source.teams || [], false)
            setTypeName(`Копия ${source.distribution_type}`)
            setRows(copiedRows)
            setCopySourceLoaded(true)
            setInitialRows([])
          } else {
            setRows(buildDefaultCreateRows(loadedTeamOptions))
            setInitialRows([])
          }
        } else {
          const rulesRes = await api.get('/admin/budget/budget-rules')
          const target = (rulesRes.data?.grouped || []).find((g) => (g.distribution_type || '') === decodedParamType || g.distribution_type_id === decodedParamType)
          if (!mounted) return
          if (!target) {
            setError('Тип распределения не найден')
            setRows([])
            setInitialRows([])
            return
          }
          if (target) {
            setTypeName(target.distribution_type)
            if (target.distribution_type_id && target.distribution_type_id !== decodedParamType) {
              navigate(`/admin/budget/budget-rules/${target.distribution_type_id}/edit${location.search}`, { replace: true, state: location.state })
            }
          }
          const mappedRows = mapBudgetRuleRows(target?.teams || [], true)
          setRows(mappedRows.length > 0 ? mappedRows : [{ id: null, team: '', distribution_category: 'team_income', percentage: '' }])
          setInitialRows(mappedRows)
        }
      } catch (e) {
        setError(translateApiError(e.response?.data?.error || e.message))
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [isCreateMode, decodedParamType, copySourceType])

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }

  const addRow = () => {
    setRows((prev) => [...prev, { id: null, team: '', distribution_category: 'production', percentage: '0' }])
  }

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index))
  }

  const validateRows = () => {
    if (rows.length === 0) return 'Добавьте хотя бы одну строку правила'
    for (const row of rows) {
      const team = (row.team || '').trim()
      const percentage = row.percentage === '' ? NaN : Number(row.percentage)
      if (!team) return 'Во всех строках укажите команду / тип расхода'
      if (Number.isNaN(percentage) || percentage < 0 || percentage > 100) {
        return 'Во всех строках процент должен быть от 0 до 100'
      }
    }
    const uniqueCheck = new Set()
    for (const row of rows) {
      const key = (row.team || '').trim().toLowerCase()
      if (uniqueCheck.has(key)) {
        return 'Есть дублирующиеся строки с одинаковым названием команды/расхода'
      }
      uniqueCheck.add(key)
    }
    return ''
  }

  const goBackToStageIfNeeded = (createdType) => {
    const returnTo = location.state?.returnTo
    const stageDraft = location.state?.stageDraft
    const stageCustomDraft = location.state?.stageCustomDraft
    if (!returnTo || !stageDraft) return false
    navigate(returnTo, {
      state: {
        stageDraft: {
          ...stageDraft,
          distribution_type: createdType
        },
        stageCustomDraft: stageCustomDraft && typeof stageCustomDraft === 'object' ? stageCustomDraft : {}
      }
    })
    return true
  }

  const handleCreate = async () => {
    if (copySourceType && !copySourceLoaded) return
    const trimmedName = (typeName || '').trim()
    if (!trimmedName) {
      setError('Укажите название типа распределения')
      return
    }
    const rowError = validateRows()
    if (rowError) {
      setError(rowError)
      return
    }
    try {
      setSaving(true)
      setError('')
      const createTypeRes = await api.post('/admin/budget/distribution-types', { name: trimmedName })
      const createdType = createTypeRes.data?.distribution_type || trimmedName

      const groupedRes = await api.get('/admin/budget/budget-rules')
      const autoRows = (groupedRes.data?.grouped || []).find((g) => g.distribution_type === createdType)?.teams || []
      const autoRowsByTeam = new Map(
        autoRows
          .filter((r) => r.id != null)
          .map((r) => [(r.team || '').trim().toLowerCase(), r])
      )
      const usedAutoRuleIds = new Set()
      for (const r of rows) {
        const payload = {
          team: (r.team || '').trim(),
          distribution_type: createdType,
          distribution_category: r.distribution_category || 'team_income',
          percentage: Number(r.percentage)
        }
        const existingAutoRow = autoRowsByTeam.get(payload.team.toLowerCase())
        if (existingAutoRow) {
          usedAutoRuleIds.add(existingAutoRow.id)
          await api.put(`/admin/budget/budget-rules/${existingAutoRow.id}`, payload)
        } else {
          await api.post('/admin/budget/budget-rules', payload)
        }
      }
      for (const r of autoRows) {
        if (r.id != null && !usedAutoRuleIds.has(r.id)) {
          await api.delete(`/admin/budget/budget-rules/${r.id}`)
        }
      }

      if (!goBackToStageIfNeeded(createdType)) {
        navigate(`/admin/budget/budget-rules/${encodeURIComponent(createTypeRes.data?.id || createdType)}/edit`)
      }
    } catch (e) {
      setError(translateApiError(e.response?.data?.error || e.message))
    } finally {
      setSaving(false)
    }
  }

  const handleSaveEdit = async () => {
    const rowError = validateRows()
    if (rowError) {
      setError(rowError)
      return
    }
    try {
      setSaving(true)
      setError('')
      const byIdInitial = new Map(initialRows.filter((r) => r.id != null).map((r) => [r.id, r]))
      const byIdCurrent = new Map(rows.filter((r) => r.id != null).map((r) => [r.id, r]))

      const removed = initialRows.filter((r) => r.id != null && !byIdCurrent.has(r.id))
      for (const r of removed) {
        await api.delete(`/admin/budget/budget-rules/${r.id}`)
      }

      for (const row of rows) {
        const payload = {
          team: (row.team || '').trim(),
          distribution_type: typeName,
          distribution_category: row.distribution_category || 'team_income',
          percentage: Number(row.percentage)
        }
        if (row.id == null) {
          await api.post('/admin/budget/budget-rules', payload)
          continue
        }
        const initial = byIdInitial.get(row.id)
        const changed = !initial ||
          (initial.team || '').trim() !== payload.team ||
          (initial.distribution_category || 'team_income') !== payload.distribution_category ||
          Number(initial.percentage) !== Number(payload.percentage)
        if (changed) {
          await api.put(`/admin/budget/budget-rules/${row.id}`, payload)
        }
      }
      navigate('/admin/budget/budget-rules')
    } catch (e) {
      setError(translateApiError(e.response?.data?.error || e.message))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="loading">Загрузка...</div>

  return (
    <div className="admin-page">
      <div className="section-card">
        <div className="section-card-header">
          <h2>{isCreateMode ? 'Создание типа распределения' : `Редактирование: ${typeName}`}</h2>
        </div>
        {error && <p className="modal-error" style={{ marginBottom: 'var(--spacing-md)' }}>{error}</p>}

        {isCreateMode && (
          <div style={{ marginBottom: 'var(--spacing-xl)' }}>
            <FormGroup label="Название типа распределения">
              <Input
                value={typeName}
                onChange={(e) => setTypeName(e.target.value)}
                placeholder="Например: ЭП, РП"
                disabled={saving}
              />
            </FormGroup>
          </div>
        )}

        <div className="admin-table-frame admin-table-frame--plain-first budget-rule-table-frame">
          <div className="table-responsive">
            <table className="stage-budget-table budget-rule-stage-table" aria-label="Правила бюджета">
              <colgroup>
                <col className="budget-rule-stage-table__col-name" />
                <col className="budget-rule-stage-table__col-type" />
                <col className="budget-rule-stage-table__col-percent" />
                <col className="budget-rule-stage-table__col-action" />
              </colgroup>
              <thead>
                <tr>
                  <th>Название расхода / команда</th>
                  <th>Тип расхода</th>
                  <th>Процент</th>
                  <th aria-label="Действие"></th>
                </tr>
              </thead>
              <tbody>
                {viewRows.map((item) => {
                  if (item.kind === 'section') {
                    return (
                      <tr key={item.key} className="stage-budget-row-section">
                        <td colSpan={4}>{item.label}</td>
                      </tr>
                    )
                  }

                  const row = item.row
                  const index = rows.indexOf(row)
                  const category = row.distribution_category || 'team_income'
                  const isTeam = category === 'team_income' && isKnownTeamRow(row, teamOptions)
                  const isManualExpense = isManualExpenseRow(row, teamOptions)
                  const expenseKind = isTeam ? '' : getBudgetExpenseKind(row)
                  const rowClass = [
                    isTeam ? 'stage-budget-row-team' : (expenseKind === 'tax' ? 'stage-budget-row-tax' : 'stage-budget-row-expense'),
                    !isTeam && expenseKind ? `stage-budget-expense--${expenseKind}` : '',
                    !isTeam && expenseKind ? `budget-rule-row--${expenseKind}` : '',
                    isManualExpense ? 'budget-rule-row--manual' : '',
                  ].filter(Boolean).join(' ')
                  return (
                    <tr key={row.id != null ? row.id : `new-${index}`} className={rowClass}>
                      <td>
                        <span className="stage-budget-category budget-rule-name-cell">
                          <span className={`stage-budget-category__icon ${isTeam ? `stage-budget-category__icon--${getDepartmentTone(row.team)}` : `stage-budget-category__icon--expense stage-budget-category__icon--expense-${expenseKind || 'other'}`}`}>
                            {isTeam ? String(row.team || '—').slice(0, 1).toUpperCase() : getBudgetExpenseMarker(expenseKind)}
                          </span>
                          {isManualExpense ? (
                            <>
                              <Input
                                value={row.team}
                                onChange={(e) => updateRow(index, { team: e.target.value })}
                                placeholder="Название расхода"
                                disabled={saving}
                                className="budget-rule-inline-input"
                              />
                            </>
                          ) : (
                            <span>{row.team}</span>
                          )}
                        </span>
                      </td>
                      <td>
                        {isManualExpense ? (
                          <Select
                            aria-label="Тип расхода"
                            value={category === 'team_income' ? 'production' : category}
                            onChange={(e) => updateRow(index, { distribution_category: e.target.value })}
                            disabled={saving}
                            className="budget-rule-inline-select"
                          >
                            {expenseCategoryOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </Select>
                        ) : (
                          <span className="budget-rule-type-badge">{getCategoryLabel(categoryOptions, category)}</span>
                        )}
                      </td>
                      <td className="stage-budget-align-right">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          value={row.percentage}
                          onChange={(e) => updateRow(index, { percentage: e.target.value })}
                          disabled={saving}
                          className="budget-rule-percent-input"
                        />
                      </td>
                      <td className="budget-rule-action-cell">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTargetId(index)}
                          disabled={saving}
                          aria-label="Удалить строку"
                          title="Удалить строку"
                          className="budget-rule-delete"
                        >
                          ×
                        </Button>
                      </td>
                    </tr>
                  )
                })}
                <tr className="stage-budget-row-total budget-rule-total-row">
                  <td colSpan={2}>Итого по процентам</td>
                  <td className="stage-budget-align-right">{formatPercentTotal(percentageTotal)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-md)', flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={addRow} disabled={saving}>+ Добавить расход</Button>
          <Button
            variant="primary"
            onClick={isCreateMode ? handleCreate : handleSaveEdit}
            disabled={saving || (Boolean(copySourceType) && !copySourceLoaded)}
          >
            {saving ? 'Сохранение…' : (isCreateMode ? 'Создать тип распределения' : 'Сохранить изменения')}
          </Button>
        </div>
      </div>

      {deleteTargetId != null && (
        <ConfirmModal
          title="Удалить строку?"
          message={<p>Строка правила бюджета будет удалена из формы.</p>}
          confirmLabel="Удалить"
          confirmVariant="danger"
          onConfirm={() => {
            removeRow(deleteTargetId)
            setDeleteTargetId(null)
          }}
          onCancel={() => setDeleteTargetId(null)}
        />
      )}
    </div>
  )
}

export default BudgetRuleTypeForm
